// Sugalarm — shared two-page swipe-pager gesture contract (cycle 122).
//
// Extracted verbatim from the cycle-114 landscape <IobPager> so the
// portrait card↔chart pager (cycle 122) reuses the exact same proven
// tap-vs-swipe behavior instead of inventing a new gesture. <IobPager>
// now consumes this hook too, byte-identically.
//
// Contract: plain PointerEvents, no library. A horizontal-dominant drag of
// |dx| > 40px commits a page flip; < 8px is a tap that FALLS THROUGH to
// the underlying control (rings → ActiveBolusesModal, dots/markers →
// popover, the basal "Log Lantus" affordance, etc.); a vertical-dominant
// move is left to the container's native scroll (`touch-action: pan-y`).
// Two pages only (0 | 1). A committed flip fires a cycle-115 selection
// haptic. Page state is session-local and starts on page 0 (page 1 in the
// UI) on mount.

import { useRef, useState } from "react";

import { hapticSelect } from "../lib/haptics";

const SWIPE_COMMIT_PX = 40;
const SWIPE_SLOP_PX = 8;

export interface PagerSwipe {
  page: 0 | 1;
  /** Programmatic navigation (page dots). Fires a haptic on an actual
   *  change, matching a swipe commit. */
  goToPage: (p: 0 | 1) => void;
  /** Spread onto the swipe viewport element. */
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: () => void;
}

export function usePagerSwipe(): PagerSwipe {
  const [page, setPage] = useState<0 | 1>(0);
  // Cycle 115: a page commit (swipe or dot tap) fires a selection haptic.
  const goToPage = (p: 0 | 1) => {
    setPage((prev) => {
      if (prev !== p) hapticSelect();
      return p;
    });
  };

  const startRef = useRef<{ x: number; y: number; id: number } | null>(null);
  const horizRef = useRef(false);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    startRef.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
    horizRef.current = false;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const s = startRef.current;
    if (!s) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (!horizRef.current) {
      if (Math.abs(dx) > SWIPE_SLOP_PX && Math.abs(dx) > Math.abs(dy)) {
        // Commit to a horizontal gesture: capture so the rest of the
        // drag lands here, and suppress the would-be tap on the content.
        horizRef.current = true;
        try {
          (e.currentTarget as HTMLElement).setPointerCapture(s.id);
        } catch {
          /* capture is best-effort */
        }
      } else if (Math.abs(dy) > SWIPE_SLOP_PX && Math.abs(dy) >= Math.abs(dx)) {
        // Vertical-dominant → let the container scroll natively; bail.
        startRef.current = null;
        return;
      }
    }
    if (horizRef.current) e.preventDefault();
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const s = startRef.current;
    if (!s) return;
    const dx = e.clientX - s.x;
    const wasHoriz = horizRef.current;
    startRef.current = null;
    horizRef.current = false;
    if (!wasHoriz) return; // a tap — the underlying control handles it
    if (Math.abs(dx) < SWIPE_COMMIT_PX) return; // not far enough to flip
    // Swipe left (dx < 0) → page 2; swipe right → page 1.
    goToPage(dx < 0 ? 1 : 0);
  };

  const onPointerCancel = () => {
    startRef.current = null;
    horizRef.current = false;
  };

  return {
    page,
    goToPage,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  };
}
